import { Alert, Card, Col, Container, ListGroup, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import {
  HELP_TEXT,
  ROLE_ASSET_KIND_HELP,
  ROLE_ASSET_STATUS_HELP,
} from '../../components/claude-code/agentTelemetryHelp';
import { HelpBadge, HelpLabel } from '../../components/claude-code/HelpTooltip';
import { useAgentRoleAssets } from '../../apps/apis/claudeCodeDashboard/hooks';
import type { AgentRoleAssetRow } from '../../apps/apis/claudeCodeDashboard/types';
import { ROUTES } from '../../routes/routes';
import { kindLabel, ROLE_OPTIONS, roleLabel, statusVariant } from './roleAssetUi';

const AgentRoleAssetsByRolePage = () => (
  <ClaudeCodeLoginGate>
    {(session) => (
      <Container fluid className="py-4">
        <div className="d-flex align-items-end justify-content-between gap-2 mb-3 flex-wrap">
          <div>
            <Link to={ROUTES.agentTelemetry.roleAssets} className="small text-muted">
              Back to Role Asset Library
            </Link>
            <h2 className="mb-1">
              <HelpLabel description="Coverage view showing which active shared assets each role receives today.">
                Role Asset Coverage
              </HelpLabel>
            </h2>
            <div className="text-muted small">
              Signed in as <strong>{session.email}</strong>
              {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
            </div>
          </div>
        </div>
        {session.isAdmin ? <RoleCoverageBody /> : <Alert variant="warning">Role Asset Library is admin-only.</Alert>}
      </Container>
    )}
  </ClaudeCodeLoginGate>
);

function RoleCoverageBody() {
  const query = useAgentRoleAssets({ status: 'active', limit: 250 });
  if (query.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (query.error) {
    return <Alert variant="danger">Failed to load role asset coverage: {(query.error as Error).message}</Alert>;
  }
  const assets = query.data?.assets ?? [];
  const companyWideAssets = assets.filter((asset) => asset.scope === 'company_wide');
  const roleAssets = assets.filter((asset) => asset.scope !== 'company_wide');

  return (
    <>
      <Alert variant="info">
        This view shows active assets employees receive today. Draft and retired assets stay visible in the main
        library.
      </Alert>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center gap-2">
          <div className="fw-semibold">
            <HelpLabel description={HELP_TEXT.companyWideAssets}>Company-wide assets inherited by every role</HelpLabel>
          </div>
          <HelpBadge bg="secondary" description="Active company-wide asset count.">
            {companyWideAssets.length.toLocaleString()}
          </HelpBadge>
        </Card.Header>
        <Card.Body className="p-0">
          <AssetList assets={companyWideAssets} emptyText="No active company-wide assets." />
        </Card.Body>
      </Card>
      <Row className="g-3">
        {ROLE_OPTIONS.map((role) => {
          const matchingAssets = roleAssets.filter((asset) => asset.roles.includes(role.value));
          return (
            <Col xl={6} key={role.value}>
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center gap-2">
                  <div>
                    <div className="fw-semibold">
                      <HelpLabel description="Assets targeted to employees who selected this role pack.">
                        {role.label}
                      </HelpLabel>
                    </div>
                    <Link
                      to={`${ROUTES.agentTelemetry.roleAssets}?role=${encodeURIComponent(role.value)}`}
                      className="small"
                    >
                      Open filtered library
                    </Link>
                  </div>
                  <HelpBadge bg="secondary" description="Active role-specific asset count for this role.">
                    {matchingAssets.length.toLocaleString()}
                  </HelpBadge>
                </Card.Header>
                <Card.Body className="p-0">
                  <AssetList
                    assets={matchingAssets}
                    emptyText={`No active role-specific assets for ${roleLabel(role.value)}.`}
                  />
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );
}

function AssetList({ assets, emptyText }: { assets: AgentRoleAssetRow[]; emptyText: string }) {
  return assets.length ? (
    <ListGroup variant="flush">
      {assets.map((asset) => (
        <ListGroup.Item key={asset.assetId}>
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div>
              <Link to={ROUTES.agentTelemetry.roleAssetDetail(asset.assetId)} className="fw-semibold">
                {asset.title}
              </Link>
              <div className="text-muted small">
                <HelpLabel description={ROLE_ASSET_KIND_HELP[asset.kind]}>{kindLabel(asset.kind)}</HelpLabel> ·{' '}
                {asset.adapter} · v{asset.activeVersion ?? asset.latestVersion}
              </div>
            </div>
            <HelpBadge bg={statusVariant(asset.status)} description={ROLE_ASSET_STATUS_HELP[asset.status]}>
              {asset.status}
            </HelpBadge>
          </div>
        </ListGroup.Item>
      ))}
    </ListGroup>
  ) : (
    <div className="p-3 text-muted small">{emptyText}</div>
  );
}

export default AgentRoleAssetsByRolePage;
